import { registerPlugin } from '@capacitor/core'
import type { PluginListenerHandle } from '@capacitor/core'

export interface AdManagerConfig {
  appId: string
  androidAdUnitId?: string
  iosAdUnitId?: string
  testMode?: boolean
}

export interface NativeAdData {
  headline?: string
  body?: string
  callToAction?: string
  advertiser?: string
  starRating?: number
  store?: string
  price?: string
  iconImageUrl?: string
  images?: string[]
  videoUrl?: string
  adUnitId: string
}

export interface LoadBannerAdResult {
  viewId: string
}

export interface PositionBannerOptions {
  viewId: string
  x: number
  y: number
  width: number
  height: number
}

export interface AdManagerPlugin {
  /**
   * Configure personalized advertising and optionally request iOS ATT authorization.
   */
  configureAdvertisingConsent(options: {
    personalizedAds: boolean
    requestTrackingAuthorization?: boolean
  }): Promise<{ personalizedAds: boolean, trackingStatus?: string }>

  /**
   * Initialize the Ad Manager SDK
   */
  initialize(options: AdManagerConfig): Promise<void>

  /**
   * Load a native ad
   */
  loadNativeAd(options: { adUnitId?: string }): Promise<NativeAdData>

  /**
   * Load a GAM banner ad (300x250)
   */
  loadBannerAd(options: { adUnitId: string }): Promise<LoadBannerAdResult>

  /**
   * Position the banner at the given coordinates (viewport-relative, CSS pixels)
   */
  positionBanner(options: PositionBannerOptions): Promise<void>

  /**
   * Destroy a banner ad
   */
  destroyBanner(options: { viewId: string }): Promise<void>

  /**
   * Show a GAM sticky banner (320x50) at bottom of screen
   * @param bottomOffset - Height of bottom nav bar in CSS px (measured from #bottom-bar). Used to position ad just above it.
   */
  showStickyBanner(options: { adUnitId: string; bottomOffset?: number }): Promise<void>

  /**
   * Update the sticky banner bottom offset (e.g. when bottom bar height changes)
   */
  updateStickyBannerBottomOffset(options: { bottomOffset: number }): Promise<void>

  /**
   * Hide the sticky banner (destroys the ad view)
   */
  hideStickyBanner(): Promise<void>

  /**
   * Set sticky banner visibility without destroying (mask/unmask for navigation).
   * Use when temporarily hiding (e.g. FlyerPage reader) to avoid ad refresh on return.
   */
  setStickyBannerVisibility(options: { visible: boolean }): Promise<void>

  /**
   * Track ad click (call this when user clicks the ad)
   */
  trackClick(options: { adUnitId: string }): Promise<void>

  /**
   * Track ad impression (call this when ad is displayed)
   */
  trackImpression(options: { adUnitId: string }): Promise<void>

  /**
   * Add listener for ad events
   */
  addListener(
    eventName: 'adLoaded' | 'adFailedToLoad' | 'adClicked' | 'adImpression' | 'bannerAdLoaded' | 'bannerAdFailedToLoad',
    listenerFunc: (data: any) => void,
  ): Promise<PluginListenerHandle>

  /**
   * Remove all listeners
   */
  removeAllListeners(): Promise<void>
}

// Web implementation
class AdManagerWeb implements AdManagerPlugin {
  async configureAdvertisingConsent(options: {
    personalizedAds: boolean
    requestTrackingAuthorization?: boolean
  }): Promise<{ personalizedAds: boolean, trackingStatus?: string }> {
    return { personalizedAds: options.personalizedAds }
  }

  async initialize(_options: AdManagerConfig): Promise<void> {
    console.warn('AdManager is not supported on web platform')
  }

  async loadNativeAd(_options: { adUnitId?: string }): Promise<NativeAdData> {
    throw new Error('AdManager is not supported on web platform')
  }

  async loadBannerAd(_options: { adUnitId: string }): Promise<LoadBannerAdResult> {
    console.warn('AdManager loadBannerAd is not supported on web platform')
    return { viewId: '' }
  }

  async positionBanner(_options: PositionBannerOptions): Promise<void> {
    console.warn('AdManager positionBanner is not supported on web platform')
  }

  async destroyBanner(_options: { viewId: string }): Promise<void> {
    console.warn('AdManager destroyBanner is not supported on web platform')
  }

  async showStickyBanner(_options: { adUnitId: string; bottomOffset?: number }): Promise<void> {
    // No-op: sticky banner is iOS/Android only
  }

  async updateStickyBannerBottomOffset(_options: { bottomOffset: number }): Promise<void> {
    // No-op: sticky banner is iOS/Android only
  }

  async hideStickyBanner(): Promise<void> {
    // No-op: sticky banner is iOS/Android only
  }

  async setStickyBannerVisibility(_options: { visible: boolean }): Promise<void> {
    // No-op: sticky banner is iOS/Android only
  }

  async trackClick(_options: { adUnitId: string }): Promise<void> {
    console.warn('AdManager trackClick is not supported on web platform')
  }

  async trackImpression(_options: { adUnitId: string }): Promise<void> {
    console.warn('AdManager trackImpression is not supported on web platform')
  }

  async addListener(_eventName: string, _listenerFunc: (data: any) => void): Promise<any> {
    return Promise.resolve({ remove: () => {} })
  }

  async removeAllListeners(): Promise<void> {
    // No-op on web
  }
}

// Register the plugin
export const AdManager = registerPlugin<AdManagerPlugin>('AdManager', {
  web: () => Promise.resolve(new AdManagerWeb()),
})



